import React, {
  useCallback,
  useMemo,
  useEffect,
  useRef,
  useState,
} from "react";
import { Box, Typography, Paper } from "@mui/material";
import ResizableNoteNode from "./ResizableNoteNode";
import NodeToolbar from "./NodeToolbar";
import DeleteableEdge from "./DeleteableEdge";
import { useSocket } from "../hooks/useSocket";
import { NoteNodeData, ResizableNoteNodeRef } from "../types";
import RetrospectiveHeader from "./RetrospectiveHeader";

// Import the common hooks
import { useRetrospectiveData } from "../hooks/useRetrospectiveData";
import { useRetrospectiveHandlers } from "../hooks/useRetrospectiveHandlers";
import { useRetrospectiveSocketListeners } from "../hooks/useRetrospectiveSocket";
import { useRetrospectiveRendering } from "../hooks/useRetrospectiveRendering";
import LiveCursor from "./LiveCursor";
import DraggingNodePreview from "./DraggingNodePreview";

interface SailboatRetrospectiveProps {
  sprintId: string;
  sprintName: string;
  reactFlow: any;
  onBack: () => void;
  templateId: string;
}

const SailboatRetrospectiveInner: React.FC<SailboatRetrospectiveProps> = ({
  sprintId,
  sprintName,
  reactFlow,
  onBack,
}) => {
  const {
    ReactFlow,
    addEdge,
    Background,
    Controls,
    MiniMap,
    useNodesState,
    useEdgesState,
    useReactFlow,
  } = reactFlow;

  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesState] = useEdgesState([]);
  const [hoveredNodeId, setHoveredNodeId] = useState<string | null>(null);
  const [isReactFlowReady, setIsReactFlowReady] = useState(false);
  const [viewport, setViewport] = useState({ x: 0, y: 0, zoom: 1 });
  const [draggingSectionId, setDraggingSectionId] = useState<string | null>(
    null
  );
  const [originalChildPositions, setOriginalChildPositions] = useState<
    Map<string, { x: number; y: number }>
  >(new Map());

  // Add these new state variables for better drag tracking
  const [isDraggingSection, setIsDraggingSection] = useState(false);
  const [lastSectionDragPosition, setLastSectionDragPosition] = useState<{
    x: number;
    y: number;
  } | null>(null);

  // Add hover timeout ref for debouncing
  const hoverTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const reactFlowInstance = useReactFlow();
  const flowRef = useRef<HTMLDivElement>(null);
  const lastViewportRef = useRef<any>(null);
  const nodeRefs = useRef<Map<string, ResizableNoteNodeRef>>(new Map());

  // Static values for template
  const templateId = "sailboat-retrospective-v1";
  const templateName = "Sailboat Retrospective";

  // Initialize socket connection
  const {
    socket,
    connected,
    cursors,
    users,
    draggingNodes,
    activeConnections,
    editingNodes,
    emit,
    on,
    updateCursorPosition,
    updateNodeDragPosition,
    updateTextChange,
    endNodeDrag,
    sendConnectionPreview,
    endConnectionPreview,
    startNodeEditing,
    endNodeEditing,
    userId,
    userName,
    userColor,
    sendEmojiReactionAdd,
    sendEmojiReactionRemove,
    sendCommentAdd,
    sendCommentReactionAdd,
    sendCommentReactionRemove,
  } = useSocket(sprintId, sprintName);

  // Sailboat Section definitions with minHeight
  const sections = useMemo(
    () => [
      // Tailwinds (Helps) - Top Left
      {
        id: "tailwinds",
        title: "Tailwinds",
        subtitle: "What helped us move forward?",
        color: "#E8F5E8",
        childColor: "#C8E6C9",
        x: 50,
        y: 80,
        width: 480,
        minHeight: 340,
        placeholderText: "Something that helped us...",
        icon: "🌬️",
      },
      // Delightors (Good) - Top Right
      {
        id: "delightors",
        title: "Delightors",
        subtitle: "What made us feel good?",
        color: "#FFF8E1",
        childColor: "#FFF176",
        x: 580,
        y: 80,
        width: 480,
        minHeight: 340,
        placeholderText: "Something positive...",
        icon: "😊",
      },
      // Restraints (Held us back) - Bottom Left
      {
        id: "restraints",
        title: "Restraints",
        subtitle: "What held us back?",
        color: "#E3F2FD",
        childColor: "#90CAF9",
        x: 50,
        y: 470,
        width: 480,
        minHeight: 340,
        placeholderText: "Something that slowed us down...",
        icon: "⚓",
      },
      // Risks - Bottom Right
      {
        id: "risks",
        title: "Future Risks",
        subtitle: "What are future risks?",
        color: "#FCE4EC",
        childColor: "#F8BBD9",
        x: 580,
        y: 470,
        width: 480,
        minHeight: 340,
        placeholderText: "Potential future risk...",
        icon: "⚠️",
      },
      // Discussion and Action Items (Right side) - White background and no child nodes
      {
        id: "actions",
        title: "Discussion and Action Items",
        subtitle: "What should we discuss or act upon?",
        color: "#FFFFFF",
        childColor: "#F5F5F5",
        x: 1110,
        y: 80,
        width: 350,
        minHeight: 730,
        placeholderText: "Action item or discussion topic...",
        icon: "📋",
      },
    ],
    []
  );

  // Check if ReactFlow is ready
  useEffect(() => {
    if (reactFlowInstance && !isReactFlowReady) {
      const timer = setTimeout(() => {
        setIsReactFlowReady(true);
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [reactFlowInstance]);

  // Function to check if two rectangles overlap
  const isOverlapping = useCallback(
    (
      rect1: { x: number; y: number; width: number; height: number },
      rect2: { x: number; y: number; width: number; height: number }
    ) => {
      const { x: x1, y: y1, width: w1, height: h1 } = rect1;
      const { x: x2, y: y2, width: w2, height: h2 } = rect2;

      // Check horizontal overlap
      const horizontalOverlap = x1 < x2 + w2 && x1 + w1 > x2;
      // Check vertical overlap
      const verticalOverlap = y1 < y2 + h2 && y1 + h1 > y2;

      return horizontalOverlap && verticalOverlap;
    },
    []
  );

  // Function to calculate z-index based on overlap and position with higher separation
  const calculateSectionZIndex = useCallback(
    (nodesList: any[]) => {
      const sectionNodes = nodesList.filter((node) => node.type === "section");

      return nodesList.map((node) => {
        if (node.type === "section") {
          // Base z-index for sections
          let baseZIndex = 10;

          // Create rectangle for current section
          const currentRect = {
            x: node.position.x,
            y: node.position.y,
            width: node.data.width || 480,
            height: node.data.height || 340,
          };

          // Check overlaps with other sections
          const overlappingSections = sectionNodes.filter((otherSection) => {
            if (otherSection.id === node.id) return false;

            const otherRect = {
              x: otherSection.position.x,
              y: otherSection.position.y,
              width: otherSection.data.width || 480,
              height: otherSection.data.height || 340,
            };

            return isOverlapping(currentRect, otherRect);
          });

          if (overlappingSections.length > 0) {
            // If overlapping, sections with smaller y position (higher up) get higher z-index
            const allOverlappingNodes = [...overlappingSections, node];
            const sortedByY = allOverlappingNodes.sort(
              (a, b) => a.position.y - b.position.y
            );

            const currentIndex = sortedByY.findIndex(
              (sec) => sec.id === node.id
            );
            // Higher position (smaller index) gets much higher z-index with bigger separation
            baseZIndex = 1000 + (sortedByY.length - currentIndex) * 500;
          }

          return {
            ...node,
            zIndex: baseZIndex,
            style: {
              ...node.style,
              zIndex: baseZIndex,
            },
          };
        }
        return node;
      });
    },
    [isOverlapping]
  );

  // Function to ensure proper z-index for child nodes based on parent section z-index with large separation
  const ensureChildZIndex = useCallback((nodesList: any[]) => {
    const sectionNodes = nodesList.filter((node) => node.type === "section");

    return nodesList.map((node) => {
      if (node.type !== "section" && node.type !== "sailboat") {
        // Find parent section
        const parentSection = sectionNodes.find(
          (sec) => sec.data.sectionId === node.data.sectionId
        );
        if (parentSection) {
          // Child z-index should be parent z-index + 10000 to ensure it's always above with large separation
          const childZIndex = (parentSection.zIndex || 10) + 10000;
          return {
            ...node,
            zIndex: childZIndex,
            style: {
              ...node.style,
              zIndex: childZIndex,
            },
          };
        } else {
          // Default very high z-index if no parent found
          return {
            ...node,
            zIndex: 10000,
            style: {
              ...node.style,
              zIndex: 10000,
            },
          };
        }
      }
      return node;
    });
  }, []);

  // Function to calculate minimum required dimensions for a section based on its children
  const calculateMinimumSectionDimensions = useCallback(
    (sectionId: string, nodesList: any[]) => {
      const childNodes = nodesList.filter(
        (node) =>
          node.data?.sectionId === sectionId &&
          node.type !== "section" &&
          node.type !== "sailboat"
      );

      const nodeWidth = 120;
      const nodeHeight = 120;
      const paddingY = 25;
      const paddingX = 30;
      const headerHeight = 85;

      if (childNodes.length === 0) {
        // No children, return absolute minimum
        return {
          minWidth: 350, // Absolute minimum width
          minHeight: headerHeight + paddingY * 2 + 50,
        };
      }

      // Calculate minimum width needed to fit at least one child per row
      const minWidth = Math.max(350, nodeWidth + paddingX * 2);

      // Calculate minimum height based on actual child positions instead of grid assumption
      const parentSection = nodesList.find(
        (node) => node.type === "section" && node.data.sectionId === sectionId
      );

      if (!parentSection) {
        return {
          minWidth,
          minHeight:
            sections.find((sec) => sec.id === sectionId)?.minHeight || 340,
        };
      }

      // Find the bottommost child relative to the section
      let maxBottomY = 0;
      childNodes.forEach((child) => {
        const relativeY = child.position.y - parentSection.position.y;
        const bottomY = relativeY + nodeHeight;
        if (bottomY > maxBottomY) {
          maxBottomY = bottomY;
        }
      });

      // Add some bottom padding
      const calculatedMinHeight = maxBottomY + paddingY + 30;
      const originalMinHeight =
        sections.find((sec) => sec.id === sectionId)?.minHeight || 340;

      return {
        minWidth,
        minHeight: Math.max(calculatedMinHeight, originalMinHeight),
      };
    },
    [sections]
  );

  // Function to calculate dynamic height for sections based on actual child node positions
  const calculateSectionHeight = useCallback(
    (sectionId: string, nodesList: any[], sectionWidth?: number) => {
      const childNodes = nodesList.filter(
        (node) =>
          node.data?.sectionId === sectionId &&
          node.type !== "section" &&
          node.type !== "sailboat"
      );

      const nodeHeight = 120;
      const paddingY = 25;
      const headerHeight = 85;

      // If no children, return minimum height
      if (childNodes.length === 0) {
        const section = sections.find((sec) => sec.id === sectionId);
        return section?.minHeight || 340;
      }

      // Find the parent section to calculate relative positions
      const parentSection = nodesList.find(
        (node) => node.type === "section" && node.data.sectionId === sectionId
      );

      if (!parentSection) {
        const section = sections.find((sec) => sec.id === sectionId);
        return section?.minHeight || 340;
      }

      // Calculate height based on actual child positions, not grid layout
      let maxBottomY = 0;

      childNodes.forEach((child) => {
        // Calculate relative position to the section
        const relativeY = child.position.y - parentSection.position.y;
        const bottomY = relativeY + nodeHeight;

        if (bottomY > maxBottomY) {
          maxBottomY = bottomY;
        }
      });

      // Add some bottom padding
      const totalHeight = maxBottomY + paddingY + 50;

      // Ensure it's not smaller than the original minimum height
      const section = sections.find((sec) => sec.id === sectionId);
      return Math.max(totalHeight, section?.minHeight || 340);
    },
    [sections]
  );

  // Function to update child positions and ensure they stay within parent bounds
  const updateChildPositions = useCallback(
    (nodesList: any[], forceReorganize = false) => {
      const sectionNodes = nodesList.filter((node) => node.type === "section");
      const childNodes = nodesList.filter(
        (node) => node.type !== "section" && node.type !== "sailboat"
      );

      const updatedNodes = nodesList.map((node) => {
        if (node.type !== "section" && node.type !== "sailboat") {
          // Find parent section
          const parentSection = sectionNodes.find(
            (sec) => sec.data.sectionId === node.data.sectionId
          );

          if (parentSection && forceReorganize) {
            // Only reorganize if forced (like on resize or add new node)
            // Calculate relative position within parent
            const nodeWidth = 120;
            const nodeHeight = 120;
            const horizontalSpacing = 35;
            const verticalSpacing = 35;
            const paddingX = 30;
            const paddingY = 25;
            const headerHeight = 85;

            // Calculate nodes per row based on parent section width
            const availableWidth =
              (parentSection.data.width || 480) - paddingX * 2;
            const nodesPerRow = Math.max(
              1,
              Math.floor(
                (availableWidth + horizontalSpacing) /
                  (nodeWidth + horizontalSpacing)
              )
            );

            // Get all siblings in same section, sorted by their current index or creation time
            const siblings = childNodes
              .filter((n) => n.data.sectionId === node.data.sectionId)
              .sort((a, b) => {
                // Try to maintain existing order based on position
                const aY = a.position.y - parentSection.position.y;
                const bY = b.position.y - parentSection.position.y;
                if (Math.abs(aY - bY) > 50) {
                  // Different rows
                  return aY - bY;
                }
                // Same row, sort by x
                const aX = a.position.x - parentSection.position.x;
                const bX = b.position.x - parentSection.position.x;
                return aX - bX;
              });

            const nodeIndex = siblings.findIndex((n) => n.id === node.id);

            if (nodeIndex !== -1) {
              const row = Math.floor(nodeIndex / nodesPerRow);
              const col = nodeIndex % nodesPerRow;

              const xOffset = paddingX + col * (nodeWidth + horizontalSpacing);
              const yOffset =
                headerHeight + paddingY + row * (nodeHeight + verticalSpacing);
              const xPosition = parentSection.position.x + xOffset;
              const yPosition = parentSection.position.y + yOffset;

              return {
                ...node,
                position: {
                  x: xPosition,
                  y: yPosition,
                },
              };
            }
          } else if (parentSection && !forceReorganize) {
            // Ensure child nodes stay within parent bounds even without reorganization
            const nodeWidth = 120;
            const nodeHeight = 120;
            const paddingX = 30;
            const paddingY = 25;
            const headerHeight = 85;

            // Calculate boundaries
            const minX = parentSection.position.x + paddingX;
            const maxX =
              parentSection.position.x +
              (parentSection.data.width || 480) -
              nodeWidth -
              paddingX;
            const minY = parentSection.position.y + headerHeight + paddingY;
            const maxY =
              parentSection.position.y +
              (parentSection.data.height || 340) -
              nodeHeight -
              paddingY;

            // Check if current position is outside bounds
            let newX = node.position.x;
            let newY = node.position.y;

            // Constrain X position
            if (newX < minX) newX = minX;
            if (newX > maxX) newX = maxX;

            // Constrain Y position
            if (newY < minY) newY = minY;
            if (newY > maxY) newY = maxY;

            // Only update position if it was constrained
            if (newX !== node.position.x || newY !== node.position.y) {
              return {
                ...node,
                position: {
                  x: newX,
                  y: newY,
                },
              };
            }
          }
        }
        return node;
      });

      // Apply section z-index calculations first, then child z-index
      const withSectionZIndex = calculateSectionZIndex(updatedNodes);
      return ensureChildZIndex(withSectionZIndex);
    },
    [calculateSectionZIndex, ensureChildZIndex]
  );

  // Function to update section heights individually (NO MORE UNIFORM HEIGHT)
  const updateSectionHeightsAndSailboat = useCallback(
    (nodesList: any[]) => {
      return nodesList.map((node) => {
        if (node.type === "section") {
          // Calculate height for each section individually based on its own content
          const newHeight = calculateSectionHeight(
            node.data.sectionId,
            nodesList,
            node.data.width
          );

          // Don't move sections - keep their original positions
          // Each section stays in its original position and grows independently
          return {
            ...node,
            data: {
              ...node.data,
              height: newHeight,
            },
            style: {
              ...node.style,
              height: newHeight,
            },
          };
        } else if (node.type === "sailboat") {
          // Keep sailboat in center regardless of section heights
          // Use the original static position calculation
          const sailboatCenterX = 50 + (480 * 2 + 50) / 2 - 150; // Center horizontally between the 4 sections
          const sailboatCenterY = 80 + 340 + 25 - 115; // Use original static calculation

          return {
            ...node,
            position: {
              x: sailboatCenterX,
              y: sailboatCenterY,
            },
          };
        }
        return node;
      });
    },
    [calculateSectionHeight]
  );

  // FIXED: Function to update edge z-index to be above sections
  const updateEdgeZIndex = useCallback((edgesList: any[], nodesList: any[]) => {
    // Find the highest z-index among sections and children
    let maxZIndex = 0;
    nodesList.forEach((node) => {
      if (node.zIndex && node.zIndex > maxZIndex) {
        maxZIndex = node.zIndex;
      }
    });

    // Set edges to be above all nodes
    return edgesList.map((edge) => ({
      ...edge,
      zIndex: maxZIndex + 5000, // Ensure edges are always above all nodes
      style: {
        ...edge.style,
        zIndex: maxZIndex + 5000,
      },
    }));
  }, []);

  // Create sailboat diagram as background node
  const createSailboatNode = useCallback(() => {
    return {
      id: "sailboat-diagram",
      type: "sailboat",
      position: { x: 415, y: 315 }, // Static position
      data: {
        width: 300,
        height: 230,
      },
      style: {
        width: 300,
        height: 230,
        zIndex: 0,
      },
      draggable: false,
      selectable: false,
      zIndex: 0,
    };
  }, []);

  // Function to create default nodes for each section
  const createDefaultNodes = useCallback(() => {
    const defaultNodes: any[] = [];

    // Add sailboat diagram
    defaultNodes.push(createSailboatNode());

    sections.forEach((section) => {
      defaultNodes.push({
        id: `section-${section.id}`,
        type: "section",
        position: {
          x: section.x,
          y: section.y,
        },
        data: {
          title: section.title,
          subtitle: section.subtitle,
          color: section.color,
          width: section.width,
          height: section.minHeight,
          sectionId: section.id,
          icon: section.icon,
        },
        style: {
          width: section.width,
          height: section.minHeight,
          zIndex: 10,
        },
        draggable: true,
        selectable: true,
        zIndex: 10,
      });

      // Skip creating child nodes for actions section (it should remain empty)
      if (section.id === "actions") {
        return;
      }

      // Create child nodes with proper spacing and positioning
      const nodeWidth = 120;
      const nodeHeight = 120;
      const horizontalSpacing = 35;
      const verticalSpacing = 35;
      const paddingX = 30;
      const paddingY = 25;
      const headerHeight = 85;

      // Calculate how many nodes can fit per row with new spacing
      const availableWidth = section.width - paddingX * 2;
      const availableHeight = section.minHeight - headerHeight - paddingY * 2;
      const nodesPerRow = Math.floor(
        availableWidth / (nodeWidth + horizontalSpacing)
      );
      const maxRows = Math.floor(
        availableHeight / (nodeHeight + verticalSpacing)
      );

      // Create 6 child nodes per section
      const totalNodes = Math.min(6, nodesPerRow * maxRows);

      for (let i = 0; i < totalNodes; i++) {
        const row = Math.floor(i / nodesPerRow);
        const col = i % nodesPerRow;

        // Calculate position within section
        const xOffset = paddingX + col * (nodeWidth + horizontalSpacing);
        const yOffset =
          headerHeight + paddingY + row * (nodeHeight + verticalSpacing);

        const xPosition = section.x + xOffset;
        const yPosition = section.y + yOffset;

        const nodeId = `${section.id}-child-${i + 1}`;

        defaultNodes.push({
          id: nodeId,
          type: "resizableNote",
          position: {
            x: xPosition,
            y: yPosition,
          },
          data: {
            text: section.placeholderText,
            width: nodeWidth,
            height: nodeHeight,
            color: section.childColor,
            shape: "rectangle",
            userId: "",
            reactions: [],
            sectionId: section.id,
            isChild: true,
          } as NoteNodeData,
          style: {
            width: nodeWidth,
            height: nodeHeight,
            zIndex: 10000,
          },
          draggable: true,
          zIndex: 10000,
        });
      }
    });

    // Update section heights and sailboat position based on initial child nodes
    const withSectionHeights = updateSectionHeightsAndSailboat(defaultNodes);
    const withSectionZIndex = calculateSectionZIndex(withSectionHeights);
    return ensureChildZIndex(withSectionZIndex);
  }, [
    sections,
    createSailboatNode,
    updateSectionHeightsAndSailboat,
    calculateSectionZIndex,
    ensureChildZIndex,
  ]);

  // Function to prepare template data for API
  const prepareTemplateData = useCallback(() => {
    const canvasWidth = 1600;
    const canvasHeight = 900;

    const flowNodes = nodes.map((node) => ({
      id: node.id,
      type: node.type,
      position: node.position,
      data: {
        ...node.data,
        reactions: node.data.reactions || [],
        comments: node.data.comments || [],
      },
      style: node.style || {},
      draggable: node.draggable !== false,
      selectable: node.selectable !== false,
      zIndex: node.zIndex,
      timestamp: Date.now(),
    }));

    // FIXED: Apply proper z-index to edges
    const flowEdges = updateEdgeZIndex(edges, nodes).map((edge) => ({
      id: edge.id,
      source: edge.source,
      target: edge.target,
      sourceHandle: edge.sourceHandle,
      targetHandle: edge.targetHandle,
      type: edge.type || "deleteable",
      style: edge.style || {
        stroke: "#374151",
        strokeWidth: 2,
      },
      markerEnd: edge.markerEnd || {
        type: "arrowclosed",
        color: "#374151",
      },
      zIndex: edge.zIndex || 25000, // High z-index for edges
    }));

    const statistics = {
      totalNodes: nodes.filter(
        (n) => n.type !== "section" && n.type !== "sailboat"
      ).length,
      totalEdges: edges.length,
      totalReactions: nodes.reduce(
        (total, node) => total + (node.data.reactions?.length || 0),
        0
      ),
      totalComments: nodes.reduce(
        (total, node) => total + (node.data.comments?.length || 0),
        0
      ),
      totalCommentReactions: nodes.reduce(
        (total, node) =>
          total +
          (node.data.comments || []).reduce(
            (commentTotal, comment) =>
              commentTotal + (comment.reactions?.length || 0),
            0
          ),
        0
      ),
      userCount: new Set([
        ...nodes.map((node) => node.data.userId || userId).filter(Boolean),
        ...nodes
          .flatMap((node) => node.data.reactions || [])
          .map((reaction) => reaction.userId)
          .filter(Boolean),
        ...nodes
          .flatMap((node) => node.data.comments || [])
          .map((comment) => comment.userId)
          .filter(Boolean),
      ]).size,
      sectionStats: sections.map((section) => ({
        id: section.id,
        title: section.title,
        nodeCount: nodes.filter(
          (node) =>
            node.data.sectionId === section.id && node.type !== "section"
        ).length,
        reactionCount: nodes
          .filter(
            (node) =>
              node.data.sectionId === section.id && node.type !== "section"
          )
          .reduce(
            (total, node) => total + (node.data.reactions?.length || 0),
            0
          ),
        commentCount: nodes
          .filter(
            (node) =>
              node.data.sectionId === section.id && node.type !== "section"
          )
          .reduce(
            (total, node) => total + (node.data.comments?.length || 0),
            0
          ),
      })),
    };

    return [
      {
        flowData: {
          nodes: flowNodes,
          edges: flowEdges,
        },
        canvasMetadata: {
          width: canvasWidth,
          height: canvasHeight,
          // viewport: reactFlowInstance.getViewport(),
        },
        sections: sections,
        statistics,
        lastUpdated: new Date().toISOString(),
      },
    ];
  }, [nodes, edges, reactFlowInstance, userId, sections, updateEdgeZIndex]);

  // Format loaded nodes for sailboat retrospective
  const formatLoadedNodes = useCallback(
    (loadedNodes: any[]) => {
      const formattedNodes = loadedNodes.map((node: any) => ({
        id: node.id,
        type: node.type || "resizableNote",
        position: node.position || { x: 0, y: 0 },
        data: {
          ...node.data,
          reactions: node.data.reactions || [],
          comments: node.data.comments || [],
        },
        style: {
          ...node.style,
          zIndex:
            node.type === "section" ? 10 : node.type === "sailboat" ? 0 : 10000,
        },
        draggable:
          node.type === "sailboat"
            ? false
            : node.draggable !== undefined
            ? node.draggable
            : true,
        selectable:
          node.type === "sailboat"
            ? false
            : node.selectable !== undefined
            ? node.selectable
            : true,
        zIndex:
          node.type === "section" ? 10 : node.type === "sailboat" ? 0 : 10000,
      }));

      // Update section heights and sailboat position after loading
      const withSectionHeights =
        updateSectionHeightsAndSailboat(formattedNodes);
      const withSectionZIndex = calculateSectionZIndex(withSectionHeights);
      return ensureChildZIndex(withSectionZIndex);
    },
    [updateSectionHeightsAndSailboat, calculateSectionZIndex, ensureChildZIndex]
  );

  // FIXED: Format loaded edges with proper z-index
  const formatLoadedEdges = useCallback(
    (loadedEdges: any[]) => {
      const baseEdges = loadedEdges.map((edge: any) => ({
        id: edge.id,
        source: edge.source,
        target: edge.target,
        sourceHandle: edge.sourceHandle,
        targetHandle: edge.targetHandle,
        type: edge.type || "deleteable",
        style: edge.style || {
          stroke: "#374151",
          strokeWidth: 2,
        },
        markerEnd: edge.markerEnd || {
          type: "arrowclosed",
          color: "#374151",
        },
        zIndex: edge.zIndex || 25000,
      }));

      // Apply proper z-index to edges
      return updateEdgeZIndex(baseEdges, nodes);
    },
    [updateEdgeZIndex, nodes]
  );

  // Use common hooks
  const {
    isDataLoaded,
    setIsDataLoaded,
    lastSaveTime,
    saveStatus,
    hasPendingSave,
    markUserChange,
    saveFlowData,
    loadExistingData,
    isLoadingSprintDetails,
    isUpdatingSprint,
  } = useRetrospectiveData(
    sprintId,
    templateId,
    templateName,
    nodes,
    edges,
    reactFlowInstance,
    userId,
    prepareTemplateData
  );

  const {
    handleNodeDelete,
    handleEdgeDelete,
    handleNodeColorChange,
    handleNodeTextChange,
    handleNodeTextChangeRealtime,
    handleNodeEditStart,
    handleNodeEditEnd,
    handleNodeEmojiReactionAdd,
    handleNodeEmojiReactionToggle,
    handleAddComment,
    handleAddCommentReaction,
    handleRemoveCommentReaction,
    handleNodesChange: baseHandleNodesChange,
    handleEdgesChange,
  } = useRetrospectiveHandlers(
    nodes,
    setNodes,
    setEdges,
    edges,
    emit,
    userId,
    userName,
    userColor,
    users,
    markUserChange,
    sendEmojiReactionAdd,
    sendEmojiReactionRemove,
    sendCommentAdd,
    sendCommentReactionAdd,
    sendCommentReactionRemove,
    updateTextChange,
    startNodeEditing,
    endNodeEditing,
    updateNodeDragPosition,
    endNodeDrag,
    onNodesChange,
    onEdgesState
  );

  const { getCursorsData, getDraggingPreviewsData } = useRetrospectiveRendering(
    cursors,
    draggingNodes,
    activeConnections,
    nodes,
    users,
    viewport,
    reactFlowInstance
  );

  // Socket listeners
  useRetrospectiveSocketListeners(
    connected,
    on,
    setNodes,
    setEdges,
    reactFlowInstance,
    userId
  );

  // Add node to a specific section with dynamic height update
  const addNodeToSection = useCallback(
    (sectionId: string) => {
      const section = sections.find((sec) => sec.id === sectionId);
      const parentSection = nodes.find(
        (node) => node.type === "section" && node.data.sectionId === sectionId
      );

      if (!section || !parentSection) return;

      const sectionNodes = nodes.filter(
        (node) => node.data.sectionId === sectionId && node.type !== "section"
      );

      const nodeWidth = 120;
      const nodeHeight = 120;
      const horizontalSpacing = 35;
      const verticalSpacing = 35;
      const paddingX = 30;
      const paddingY = 25;
      const headerHeight = 85;

      // Calculate nodes per row based on current section width
      const availableWidth = (parentSection.data.width || 480) - paddingX * 2;
      const nodesPerRow = Math.max(
        1,
        Math.floor(
          (availableWidth + horizontalSpacing) / (nodeWidth + horizontalSpacing)
        )
      );

      const row = Math.floor(sectionNodes.length / nodesPerRow);
      const col = sectionNodes.length % nodesPerRow;

      const xOffset = paddingX + col * (nodeWidth + horizontalSpacing);
      const yOffset =
        headerHeight + paddingY + row * (nodeHeight + verticalSpacing);
      const xPosition = parentSection.position.x + xOffset;
      const yPosition = parentSection.position.y + yOffset;

      const newNode = {
        id: `${sectionId}-child-${Date.now()}-${userId}`,
        type: "resizableNote",
        position: {
          x: xPosition,
          y: yPosition,
        },
        data: {
          text: section.placeholderText,
          width: nodeWidth,
          height: nodeHeight,
          color: section.childColor,
          shape: "rectangle",
          userId: userId,
          reactions: [],
          sectionId: sectionId,
          isChild: true,
        } as NoteNodeData,
        style: {
          width: nodeWidth,
          height: nodeHeight,
          zIndex: 10000,
        },
        draggable: true,
        zIndex: 10000,
      };

      setNodes((nds) => {
        const updatedNodes = [...nds, newNode];
        // Update section heights and sailboat position after adding new node
        const withSectionHeights =
          updateSectionHeightsAndSailboat(updatedNodes);
        const withSectionZIndex = calculateSectionZIndex(withSectionHeights);
        return ensureChildZIndex(withSectionZIndex);
      });

      // FIXED: Update edge z-index when nodes change
      setEdges((eds) => updateEdgeZIndex(eds, [...nodes, newNode]));

      markUserChange();
      emit("NODE_ADDED", {
        node: newNode,
        addedBy: userId,
      });
    },
    [
      setNodes,
      setEdges,
      emit,
      userId,
      markUserChange,
      nodes,
      sections,
      updateSectionHeightsAndSailboat,
      calculateSectionZIndex,
      ensureChildZIndex,
      updateEdgeZIndex,
    ]
  );

  // Custom Sailboat Node Component
  const SailboatNode = useCallback(({ data }: any) => {
    return (
      <Box
        sx={{
          width: data.width,
          height: data.height,
          position: "relative",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          zIndex: 0,
          pointerEvents: "none",
        }}
      >
        <svg
          width="280"
          height="210"
          viewBox="0 0 280 210"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          {/* Water */}
          <path
            d="M0 160 C50 155, 100 165, 140 160 C180 155, 220 165, 280 160 L280 210 L0 210 Z"
            fill="#4FC3F7"
            opacity="0.3"
          />

          {/* Boat Hull */}
          <path
            d="M80 140 C120 150, 160 150, 200 140 L190 160 C160 165, 120 165, 90 160 Z"
            fill="#8D6E63"
            stroke="#5D4037"
            strokeWidth="2"
          />

          {/* Mast */}
          <line
            x1="140"
            y1="50"
            x2="140"
            y2="140"
            stroke="#5D4037"
            strokeWidth="4"
          />

          {/* Main Sail */}
          <path
            d="M50 60 C90 50, 130 55, 140 60 L140 130 C130 125, 90 120, 50 130 Z"
            fill="#FFF"
            stroke="#DDD"
            strokeWidth="2"
          />

          {/* Jib Sail */}
          <path
            d="M140 70 L210 55 L140 120 Z"
            fill="#FFF"
            stroke="#DDD"
            strokeWidth="2"
          />

          {/* Wind lines (tailwinds) */}
          <g opacity="0.5">
            <path
              d="M15 50 L35 50 M20 45 L30 45 M15 55 L35 55"
              stroke="#81C784"
              strokeWidth="2"
            />
            <path
              d="M15 80 L35 80 M20 75 L30 75 M15 85 L35 85"
              stroke="#81C784"
              strokeWidth="2"
            />
            <path
              d="M15 110 L35 110 M20 105 L30 105 M15 115 L35 115"
              stroke="#81C784"
              strokeWidth="2"
            />
          </g>

          {/* Anchor (restraints) */}
          <g transform="translate(120, 180)">
            <circle cx="0" cy="0" r="8" fill="#666" />
            <path d="M-12 0 L12 0 M0 -12 L0 12" stroke="#666" strokeWidth="3" />
            <path
              d="M-8 10 L8 10 M-8 -10 L8 -10"
              stroke="#666"
              strokeWidth="2"
            />
          </g>
        </svg>
      </Box>
    );
  }, []);

  // Custom Section Node Component with enhanced resize capability
  const SectionNode = useCallback(
    ({ data, id, selected }: any) => {
      const { NodeResizer } = reactFlow;

      const handleSectionResize = useCallback(
        (event: any, params: { width: number; height: number }) => {
          const { width, height } = params;

          setNodes((nds) => {
            // Calculate minimum required dimensions based on current children
            const { minWidth, minHeight } = calculateMinimumSectionDimensions(
              data.sectionId,
              nds
            );

            // Enforce minimum dimensions
            const constrainedWidth = Math.max(width, minWidth);
            const constrainedHeight = Math.max(height, minHeight);

            // Update section dimensions with constrained values
            const updatedNodes = nds.map((node) =>
              node.id === id
                ? {
                    ...node,
                    data: {
                      ...node.data,
                      width: constrainedWidth,
                      height: constrainedHeight,
                    },
                    style: {
                      ...node.style,
                      width: constrainedWidth,
                      height: constrainedHeight,
                    },
                  }
                : node
            );

            // Force reorganization and constraint of child positions after section resize
            const withChildPositions = updateChildPositions(updatedNodes, true);
            const withSectionHeights =
              updateSectionHeightsAndSailboat(withChildPositions);
            const withSectionZIndex =
              calculateSectionZIndex(withSectionHeights);
            return ensureChildZIndex(withSectionZIndex);
          });

          // FIXED: Update edge z-index when nodes change
          setEdges((eds) => updateEdgeZIndex(eds, nodes));

          markUserChange();
        },
        [id, data.sectionId]
      );

      // Calculate dynamic minimum dimensions based on current children
      const { minWidth, minHeight } = calculateMinimumSectionDimensions(
        data.sectionId,
        nodes
      );

      return (
        <Box
          sx={{
            width: data.width,
            height: data.height,
            backgroundColor: data.color,
            borderRadius: 3,
            position: "relative",
            display: "flex",
            flexDirection: "column",
            boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
            border: data.sectionId === "actions" ? "2px solid #E0E0E0" : "none",
            outline: selected ? "3px solid #1976d2" : "none",
            outlineOffset: selected ? "2px" : "0",
            zIndex: 1,
          }}
        >
          <NodeResizer
            color="#1976d2"
            isVisible={selected}
            minWidth={minWidth}
            minHeight={minHeight}
            onResize={handleSectionResize}
          />

          <Paper
            elevation={0}
            sx={{
              backgroundColor: "transparent",
              borderRadius: "12px 12px 0 0",
              p: 2,
              textAlign: "center",
              minHeight: 75,
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
            }}
          >
            <Typography
              variant="h6"
              component="div"
              sx={{
                fontWeight: 600,
                fontSize: "16px",
                mb: 0.5,
                color: "#000",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 1,
              }}
            >
              <span style={{ fontSize: "18px" }}>{data.icon}</span>
              {data.title}
            </Typography>
            <Typography
              variant="body2"
              sx={{
                fontSize: "12px",
                color: "#666",
                mb: 1,
              }}
            >
              {data.subtitle}
            </Typography>
            <Typography
              variant="body2"
              sx={{
                cursor: "pointer",
                fontSize: "12px",
                color: "primary.main",
                "&:hover": {
                  textDecoration: "underline",
                },
              }}
              onClick={(e) => {
                e.stopPropagation();
                addNodeToSection(data.sectionId);
              }}
            >
              + Add Note
            </Typography>
          </Paper>
          <Box
            sx={{
              flex: 1,
              position: "relative",
              padding: 1,
            }}
          />
        </Box>
      );
    },
    [
      addNodeToSection,
      reactFlow,
      setNodes,
      setEdges,
      markUserChange,
      updateChildPositions,
      updateSectionHeightsAndSailboat,
      calculateSectionZIndex,
      ensureChildZIndex,
      calculateMinimumSectionDimensions,
      nodes,
      updateEdgeZIndex,
    ]
  );

  const getClosestSection = useCallback(
    (position: { x: number; y: number }) => {
      let closestSection = null;
      let minDistance = Infinity;

      const sectionNodes = nodes.filter((node) => node.type === "section");

      sectionNodes.forEach((sectionNode) => {
        const sectionCenterX =
          sectionNode.position.x + (sectionNode.data.width || 480) / 2;
        const sectionCenterY =
          sectionNode.position.y + (sectionNode.data.height || 340) / 2;
        const distance = Math.sqrt(
          Math.pow(position.x - sectionCenterX, 2) +
            Math.pow(position.y - sectionCenterY, 2)
        );

        if (distance < minDistance) {
          minDistance = distance;
          closestSection = sections.find(
            (sec) => sec.id === sectionNode.data.sectionId
          );
        }
      });

      return closestSection;
    },
    [sections, nodes]
  );

  // Handle node hover with debouncing
  const handleNodeMouseEnter = useCallback((nodeId: string) => {
    // Clear any existing timeout
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
    }

    // Set a small delay before showing the toolbar to prevent flickering
    hoverTimeoutRef.current = setTimeout(() => {
      setHoveredNodeId(nodeId);
    }, 150);
  }, []);

  const handleNodeMouseLeave = useCallback(() => {
    // Clear the timeout if user leaves before delay completes
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
    }

    // Hide the toolbar with a small delay to allow moving to the toolbar itself
    hoverTimeoutRef.current = setTimeout(() => {
      setHoveredNodeId(null);
    }, 100);
  }, []);

  // Handle toolbar hover to keep it visible
  const handleToolbarMouseEnter = useCallback(() => {
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
    }
  }, []);

  const handleToolbarMouseLeave = useCallback(() => {
    setHoveredNodeId(null);
  }, []);

  // Clean up timeout on unmount
  useEffect(() => {
    return () => {
      if (hoverTimeoutRef.current) {
        clearTimeout(hoverTimeoutRef.current);
      }
    };
  }, []);

  // Enhanced nodes change handler with proper child constraint handling
  const handleNodesChange = useCallback(
    (changes: any[]) => {
      baseHandleNodesChange(changes, (processedChanges: any[]) => {
        let shouldUpdateSectionHeights = false;
        let shouldUpdateChildPositions = false;
        let hasSectionDragChanges = false;
        let shouldRecalculateZIndex = false;

        processedChanges.forEach((change) => {
          const node = nodes.find((n) => n.id === change.id);

          // Skip processing for section selection changes to prevent unwanted repositioning
          if (change.type === "select" && node?.type === "section") {
            // Recalculate z-index when section is selected (in case of overlaps)
            shouldRecalculateZIndex = true;
            return;
          }

          // Handle section drag start
          if (
            change.type === "position" &&
            change.dragging &&
            node?.type === "section"
          ) {
            hasSectionDragChanges = true;
            shouldRecalculateZIndex = true;

            if (!draggingSectionId) {
              setDraggingSectionId(change.id);
              setIsDraggingSection(true);
              setLastSectionDragPosition(change.position);

              // Store original child positions relative to section
              const childNodes = nodes.filter(
                (n) =>
                  n.data?.sectionId === node.data.sectionId &&
                  n.type !== "section" &&
                  n.type !== "sailboat"
              );
              const originalPositions = new Map();
              childNodes.forEach((childNode) => {
                originalPositions.set(childNode.id, {
                  x: childNode.position.x - node.position.x,
                  y: childNode.position.y - node.position.y,
                });
              });
              setOriginalChildPositions(originalPositions);
            }
          }

          // Handle section dragging - update children in real-time with z-index
          if (
            change.type === "position" &&
            change.dragging &&
            change.id === draggingSectionId &&
            node?.type === "section"
          ) {
            hasSectionDragChanges = true;
            shouldRecalculateZIndex = true;
            setLastSectionDragPosition(change.position);

            // Update child positions in real-time during drag with proper z-index
            setNodes((nds) => {
              const updatedNodes = nds.map((n) => {
                if (
                  n.data?.sectionId === node?.data.sectionId &&
                  n.type !== "section" &&
                  n.type !== "sailboat"
                ) {
                  const originalPos = originalChildPositions.get(n.id);
                  if (originalPos) {
                    return {
                      ...n,
                      position: {
                        x: change.position.x + originalPos.x,
                        y: change.position.y + originalPos.y,
                      },
                    };
                  }
                }
                return n;
              });

              // Recalculate z-index during drag
              const withSectionZIndex = calculateSectionZIndex(updatedNodes);
              return ensureChildZIndex(withSectionZIndex);
            });
          }

          // Handle section drag end
          if (
            change.type === "position" &&
            !change.dragging &&
            change.id === draggingSectionId &&
            node?.type === "section"
          ) {
            hasSectionDragChanges = true;
            shouldRecalculateZIndex = true;
            setDraggingSectionId(null);
            setIsDraggingSection(false);
            setLastSectionDragPosition(null);
            setOriginalChildPositions(new Map());

            // Update section heights and sailboat position after drag
            shouldUpdateSectionHeights = true;
            shouldRecalculateZIndex = true;
          }

          // Handle child node movement with proper boundary constraints
          if (
            change.type === "position" &&
            change.position &&
            !change.dragging &&
            !isDraggingSection &&
            node?.data?.isChild
          ) {
            const closestSection = getClosestSection(change.position);
            if (closestSection && node.data.sectionId !== closestSection.id) {
              // Store the original color before changing sections
              const originalColor = node.data.color;

              setNodes((nds) => {
                const updatedNodes = nds.map((n) =>
                  n.id === change.id
                    ? {
                        ...n,
                        data: {
                          ...n.data,
                          sectionId: closestSection.id,
                          // Keep the original color instead of changing to destination section color
                          color: originalColor,
                        },
                      }
                    : n
                );
                const withSectionHeights =
                  updateSectionHeightsAndSailboat(updatedNodes);
                // Force reorganization when moving between sections
                const withChildPositions = updateChildPositions(
                  withSectionHeights,
                  true
                );
                const withSectionZIndex =
                  calculateSectionZIndex(withChildPositions);
                return ensureChildZIndex(withSectionZIndex);
              });
              shouldUpdateSectionHeights = false;
              shouldUpdateChildPositions = false;
            } else {
              // Constrain child within parent bounds when staying in same section
              shouldUpdateSectionHeights = true;
              shouldUpdateChildPositions = true;
            }
          }

          // Check if it's a remove change (node deletion)
          if (change.type === "remove" && !isDraggingSection) {
            shouldUpdateSectionHeights = true;
            shouldRecalculateZIndex = true;
          }
        });

        // Update child positions to ensure they stay within bounds
        if (
          !isDraggingSection &&
          !hasSectionDragChanges &&
          shouldUpdateChildPositions
        ) {
          setNodes((nds) => {
            const withSectionHeights = updateSectionHeightsAndSailboat(nds);
            // Apply child position constraints
            const withChildPositions = updateChildPositions(
              withSectionHeights,
              false
            );
            const withSectionZIndex =
              calculateSectionZIndex(withChildPositions);
            return ensureChildZIndex(withSectionZIndex);
          });
        } else if (
          !isDraggingSection &&
          !hasSectionDragChanges &&
          shouldUpdateSectionHeights
        ) {
          // Update section heights if needed (but don't reorganize child positions)
          setNodes((nds) => {
            const withSectionHeights = updateSectionHeightsAndSailboat(nds);
            const withSectionZIndex =
              calculateSectionZIndex(withSectionHeights);
            return ensureChildZIndex(withSectionZIndex);
          });
        }

        // Recalculate z-index if needed (for selection, overlaps, etc.)
        if (shouldRecalculateZIndex && !hasSectionDragChanges) {
          setNodes((nds) => {
            const withSectionZIndex = calculateSectionZIndex(nds);
            return ensureChildZIndex(withSectionZIndex);
          });
        }

        // FIXED: Update edge z-index whenever nodes change
        if (
          shouldRecalculateZIndex ||
          shouldUpdateSectionHeights ||
          shouldUpdateChildPositions
        ) {
          setEdges((eds) => updateEdgeZIndex(eds, nodes));
        }
      });
    },
    [
      baseHandleNodesChange,
      nodes,
      draggingSectionId,
      originalChildPositions,
      isDraggingSection,
      getClosestSection,
      setNodes,
      setEdges,
      updateSectionHeightsAndSailboat,
      updateChildPositions,
      calculateSectionZIndex,
      ensureChildZIndex,
      updateEdgeZIndex,
    ]
  );

  // Save data when user makes changes
  useEffect(() => {
    if (isDataLoaded && hasPendingSave && nodes.length > 0) {
      saveFlowData();
    }
  }, [nodes, edges, isDataLoaded, hasPendingSave, saveFlowData]);

  // Load existing data when sprint details and ReactFlow are both ready
  useEffect(() => {
    if (!isLoadingSprintDetails && !isDataLoaded && isReactFlowReady) {
      const timer = setTimeout(() => {
        loadExistingData(
          setNodes,
          setEdges,
          createDefaultNodes,
          formatLoadedNodes,
          formatLoadedEdges
        );
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [
    isLoadingSprintDetails,
    isDataLoaded,
    isReactFlowReady,
    loadExistingData,
    setNodes,
    setEdges,
    createDefaultNodes,
    formatLoadedNodes,
    formatLoadedEdges,
  ]);

  // Handle node deletion with section height update and z-index recalculation
  const customHandleNodeDelete = useCallback(
    (nodeId: string) => {
      const node = nodes.find((n) => n.id === nodeId);
      if (node && (node.type === "section" || node.type === "sailboat")) {
        return;
      }

      setNodes((nds) => {
        const filteredNodes = nds.filter((n) => n.id !== nodeId);
        const withSectionHeights =
          updateSectionHeightsAndSailboat(filteredNodes);
        // Skip reorganization on delete to preserve manual positioning
        const withSectionZIndex = calculateSectionZIndex(withSectionHeights);
        return ensureChildZIndex(withSectionZIndex);
      });

      // FIXED: Update edge z-index when node is deleted
      setEdges((eds) => {
        const filteredEdges = eds.filter(
          (edge) => edge.source !== nodeId && edge.target !== nodeId
        );
        return updateEdgeZIndex(
          filteredEdges,
          nodes.filter((n) => n.id !== nodeId)
        );
      });

      handleNodeDelete(nodeId);

      // Hide toolbar if the deleted node was being hovered
      if (hoveredNodeId === nodeId) {
        setHoveredNodeId(null);
      }
    },
    [
      handleNodeDelete,
      nodes,
      setNodes,
      setEdges,
      updateSectionHeightsAndSailboat,
      calculateSectionZIndex,
      ensureChildZIndex,
      updateEdgeZIndex,
      hoveredNodeId,
    ]
  );

  // Handle cursor movement
  const handleNodeCursorMove = useCallback(
    (event: React.MouseEvent) => {
      if (flowRef.current && reactFlowInstance) {
        const rect = flowRef.current.getBoundingClientRect();
        const viewport = reactFlowInstance.getViewport();
        const x = (event.clientX - rect.left - viewport.x) / viewport.zoom;
        const y = (event.clientY - rect.top - viewport.y) / viewport.zoom;
        updateCursorPosition({ x, y });
      }
    },
    [reactFlowInstance, updateCursorPosition]
  );

  // Define custom node types
  const nodeTypes: any = useMemo(
    () => ({
      section: SectionNode,
      sailboat: SailboatNode,
      resizableNote: (props: any) => {
        const editingState = editingNodes.get(props.id);
        const isBeingEditedByOther =
          editingState && editingState.userId !== userId;

        return (
          <ResizableNoteNode
            {...props}
            ref={(nodeRef: any) => {
              if (nodeRef) {
                nodeRefs.current.set(props.id, nodeRef);
              } else {
                nodeRefs.current.delete(props.id);
              }
            }}
            onCursorMove={handleNodeCursorMove}
            onTextChange={handleNodeTextChange}
            onTextChangeRealtime={handleNodeTextChangeRealtime}
            onEditStart={handleNodeEditStart}
            onEditEnd={handleNodeEditEnd}
            onEmojiToggle={handleNodeEmojiReactionToggle}
            isBeingEditedByOther={isBeingEditedByOther}
            editingUser={editingState}
            currentUserId={userId}
            isSelected={hoveredNodeId === props.id}
            reactFlow={reactFlow}
            onAddComment={handleAddComment}
            onAddCommentReaction={handleAddCommentReaction}
            onRemoveCommentReaction={handleRemoveCommentReaction}
            currentUserName={userName}
            currentUserColor={userColor}
            onMouseEnter={() => handleNodeMouseEnter(props.id)}
            onMouseLeave={handleNodeMouseLeave}
          />
        );
      },
    }),
    [
      SectionNode,
      SailboatNode,
      editingNodes,
      userId,
      hoveredNodeId,
      userName,
      userColor,
      handleNodeCursorMove,
      handleNodeTextChange,
      handleNodeTextChangeRealtime,
      handleNodeEditStart,
      handleNodeEditEnd,
      handleNodeEmojiReactionToggle,
      handleAddComment,
      handleAddCommentReaction,
      handleRemoveCommentReaction,
      handleNodeMouseEnter,
      handleNodeMouseLeave,
    ]
  );

  // Define custom edge types
  const edgeTypes: any = useMemo(
    () => ({
      deleteable: (props: any) => (
        <DeleteableEdge
          {...props}
          onDelete={handleEdgeDelete}
          reactFlow={reactFlow}
        />
      ),
    }),
    [handleEdgeDelete, reactFlow]
  );

  // Handle pane click
  const handlePaneClick = useCallback(() => {
    nodeRefs.current.forEach((nodeRef, nodeId) => {
      if (nodeRef && nodeRef.isEditing) {
        nodeRef.handleSave();
      }
    });
    setHoveredNodeId(null);
  }, []);

  // Handle mouse movement
  const handleMouseMove = useCallback(
    (event: React.MouseEvent) => {
      if (flowRef.current && reactFlowInstance) {
        const rect = flowRef.current.getBoundingClientRect();
        const viewport = reactFlowInstance.getViewport();
        const x = (event.clientX - rect.left - viewport.x) / viewport.zoom;
        const y = (event.clientY - rect.top - viewport.y) / viewport.zoom;
        updateCursorPosition({ x, y });
      }
    },
    [reactFlowInstance, updateCursorPosition]
  );

  // Handle edge connections (only allow connections between child nodes)
  const onConnect: any = useCallback(
    (params: any) => {
      const sourceNode = nodes.find((n) => n.id === params.source);
      const targetNode = nodes.find((n) => n.id === params.target);

      if (!sourceNode?.data?.isChild || !targetNode?.data?.isChild) {
        return;
      }

      // FIXED: Calculate proper z-index for new edge
      let maxZIndex = 0;
      nodes.forEach((node) => {
        if (node.zIndex && node.zIndex > maxZIndex) {
          maxZIndex = node.zIndex;
        }
      });

      const newEdge = {
        id: `edge-${Date.now()}`,
        source: params.source!,
        target: params.target!,
        sourceHandle: params.sourceHandle,
        targetHandle: params.targetHandle,
        type: "deleteable",
        style: {
          stroke: "#374151",
          strokeWidth: 2,
          zIndex: maxZIndex + 5000, // Ensure new edge is above all nodes
        },
        markerEnd: {
          type: "arrowclosed",
          color: "#374151",
        },
        zIndex: maxZIndex + 5000,
      };

      setEdges((eds) => {
        const newEdges = addEdge(newEdge, eds);
        // FIXED: Update all edge z-indexes after adding new edge
        return updateEdgeZIndex(newEdges, nodes);
      });

      markUserChange();

      emit("EDGE_ADDED", {
        edge: newEdge,
        addedBy: userId,
      });
    },
    [setEdges, emit, userId, markUserChange, nodes, addEdge, updateEdgeZIndex]
  );

  // Handle viewport changes
  const handleViewportChange = useCallback(
    (newViewport: any) => {
      setViewport(newViewport);
      if (lastViewportRef.current) {
        clearTimeout(lastViewportRef.current);
      }
      lastViewportRef.current = setTimeout(() => {
        emit("VIEWPORT_CHANGED", {
          viewport: newViewport,
          changedBy: userId,
        });
      }, 100);
    },
    [emit, userId]
  );

  // Track viewport changes
  useEffect(() => {
    if (!reactFlowInstance || !isReactFlowReady) return;

    const interval = setInterval(() => {
      const currentViewport = reactFlowInstance.getViewport();
      setViewport(currentViewport);
    }, 50);

    return () => clearInterval(interval);
  }, [reactFlowInstance, isReactFlowReady]);

  // Reset function with section height updates and z-index
  const resetRetrospective = useCallback(() => {
    // Reset nodes to default
    const defaultNodes = createDefaultNodes();
    setNodes(defaultNodes);

    // Clear all edges
    setEdges([]);

    // Reset viewport to default position and zoom
    if (reactFlowInstance) {
      reactFlowInstance.setViewport({ x: 0, y: 0, zoom: 1 });
      setTimeout(() => {
        reactFlowInstance.fitView({
          padding: 0.2,
          duration: 800,
          includeHiddenNodes: false,
        });
      }, 100);
    }

    // Clear any hovered nodes
    setHoveredNodeId(null);

    // Mark as user change to trigger save
    markUserChange();

    // Emit reset event to other users
    emit("RETROSPECTIVE_RESET", {
      resetBy: userId,
      timestamp: Date.now(),
    });
  }, [
    createDefaultNodes,
    setNodes,
    setEdges,
    reactFlowInstance,
    markUserChange,
    emit,
    userId,
  ]);

  // Don't render until ReactFlow is ready and data is loaded
  if (!isReactFlowReady || !isDataLoaded) {
    return (
      <>
        <RetrospectiveHeader
          sprintName={sprintName}
          sprintId={sprintId}
          templateName={templateName}
          connected={false}
          users={users}
          saveStatus="idle"
          isLoading={true}
          lastSaveTime={null}
          onBack={onBack}
          onChangeTemplate={() => {
            console.log("Change template clicked");
          }}
        />
        <Box
          sx={{
            height: "82vh",
            width: "100%",
            position: "relative",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Typography>Loading sailboat retrospective...</Typography>
        </Box>
      </>
    );
  }

  // Render functions
  const renderCursors = () => {
    const cursorsData = getCursorsData();
    return cursorsData.map((cursor) => (
      <LiveCursor
        key={cursor.key}
        x={cursor.x}
        y={cursor.y}
        userName={cursor.userName}
        color={cursor.color}
      />
    ));
  };

  const renderDraggingPreviews = () => {
    const draggingData = getDraggingPreviewsData();
    return draggingData.map((dragging) => (
      <DraggingNodePreview
        key={dragging.key}
        x={dragging.x}
        y={dragging.y}
        width={dragging.width}
        height={dragging.height}
        color={dragging.color}
        userName={dragging.userName}
        shape={dragging.shape}
      />
    ));
  };

  return (
    <>
      <RetrospectiveHeader
        sprintName={sprintName}
        sprintId={sprintId}
        templateName={templateName}
        connected={connected}
        users={users}
        saveStatus={saveStatus}
        isLoading={isLoadingSprintDetails || isUpdatingSprint}
        lastSaveTime={lastSaveTime}
        onBack={onBack}
        onChangeTemplate={() => {
          console.log("Change template clicked");
        }}
        onReset={resetRetrospective}
      />
      <Box
        sx={{
          height: "84vh",
          width: "100%",
          position: "relative",
        }}
      >
        <div ref={flowRef} style={{ width: "100%", height: "100%" }}>
          <ReactFlow
            nodes={nodes}
            edges={edges}
            onNodesChange={handleNodesChange}
            onEdgesChange={handleEdgesChange}
            onConnect={onConnect}
            onMove={handleViewportChange}
            onMouseMove={handleMouseMove}
            onPaneClick={handlePaneClick}
            nodeTypes={nodeTypes}
            edgeTypes={edgeTypes}
            fitView={true}
            style={{ backgroundColor: "#f0f8ff" }}
            defaultEdgeOptions={{
              type: "deleteable",
              style: {
                stroke: "#374151",
                strokeWidth: 2,
                zIndex: 25000, // FIXED: High default z-index for edges
              },
              markerEnd: {
                type: "arrowclosed",
                color: "#374151",
              },
              zIndex: 25000, // FIXED: High default z-index for edges
            }}
            proOptions={{ hideAttribution: true }}
            edgesUpdatable={true}
            edgesFocusable={true}
            elementsSelectable={true}
          >
            <Background color="#ccc" gap={20} size={1} variant="dots" />
            <Controls
              showZoom={true}
              showFitView={true}
              showInteractive={true}
            />
            <MiniMap
              nodeColor={(node) => {
                if (node.type === "section")
                  return node.data?.color || "#f0f0f0";
                if (node.type === "sailboat") return "#e3f2fd";
                return node.data?.color || "#fff59d";
              }}
              nodeStrokeWidth={2}
              maskColor="rgba(240, 240, 240, 0.6)"
            />
          </ReactFlow>

          {renderCursors()}
          {renderDraggingPreviews()}

          {hoveredNodeId &&
            nodes.find((n) => n.id === hoveredNodeId)?.type !== "section" &&
            nodes.find((n) => n.id === hoveredNodeId)?.type !== "sailboat" && (
              <div
                onMouseEnter={handleToolbarMouseEnter}
                onMouseLeave={handleToolbarMouseLeave}
              >
                <NodeToolbar
                  node={nodes.find((n) => n.id === hoveredNodeId)!}
                  onColorChange={handleNodeColorChange}
                  onEmojiAdd={handleNodeEmojiReactionAdd}
                  onEmojiToggle={handleNodeEmojiReactionToggle}
                  onDelete={customHandleNodeDelete}
                  onClose={() => setHoveredNodeId(null)}
                  viewport={viewport}
                  currentUserId={userId}
                  onAddComment={handleAddComment}
                  onAddCommentReaction={handleAddCommentReaction}
                  onRemoveCommentReaction={handleRemoveCommentReaction}
                  currentUserName={userName}
                  currentUserColor={userColor}
                />
              </div>
            )}
        </div>
      </Box>
    </>
  );
};

const SailboatRetrospective: React.FC<SailboatRetrospectiveProps> = (props) => {
  const { ReactFlowProvider } = props.reactFlow;

  return (
    <ReactFlowProvider>
      <SailboatRetrospectiveInner {...props} />
    </ReactFlowProvider>
  );
};

export default SailboatRetrospective;
